import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  ApVendorDto,
  ApVendorsControllerFindMatchingV3Params,
  ApVendorsControllerGetSyncedLinkedRecordV3Params,
  ApVendorsControllerMapToClientRegisteredVendorV3Params,
  ApVendorsCreateRequestDto,
  ApVendorsFindAllForTaxIdLinkingRequestDto,
  ApVendorsFindAllForTaxIdLinkingResponseDto,
  ApVendorsFindAllRequestDto,
  ApVendorsFindAllResponseDto,
  ApVendorsGetSyncedLinkedRecordResponseDto,
  ApVendorsLinkToViveVendorRequestDto,
  ApVendorsMapToClientRegisteredVendorResponseDto,
  ApVendorsMapToExemptVendorResponseDto,
  ApVendorsUpdateOneRequestDto,
  ApVendorsUpsertRequestDto,
  TaxIdType,
  VendorType
} from './vive-api-models';

class ApVendorsService extends ViveApiBaseService {
  public async findAll(params: {
    options?: ViveApiRequestOptions;
    queryParams?: Omit<ApVendorsFindAllRequestDto, 'includeAssociations'>;
  }): Promise<ApVendorsFindAllResponseDto> {
    const { options, queryParams } = params;
    return await this.sendGet<ApVendorsFindAllResponseDto>({
      apiEndpoint: 'ap-vendors',
      options,
      queryParams
    });
  }

  public async findAllAndRemoveSyncDate(params: {
    options?: ViveApiRequestOptions;
    queryParams?: Omit<ApVendorsFindAllRequestDto, 'includeAssociations'>;
  }): Promise<ApVendorsFindAllResponseDto> {
    const { options, queryParams } = params;
    return await this.sendGet<ApVendorsFindAllResponseDto>({
      apiEndpoint: 'ap-vendors/find-and-remove-sync-date',
      options,
      queryParams
    });
  }

  public async findAllForTaxIdLinking(params: {
    options?: ViveApiRequestOptions;
    pathParams: {
      vendorType: VendorType;
      taxIdType: TaxIdType;
    };
    queryParams?: Omit<
      ApVendorsFindAllForTaxIdLinkingRequestDto,
      'includeAssociations'
    >;
  }): Promise<ApVendorsFindAllForTaxIdLinkingResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<ApVendorsFindAllForTaxIdLinkingResponseDto>({
      apiEndpoint:
        'ap-vendors/tax-id-linking/vendor-type/:vendorType/tax-id-type/:taxIdType',
      options,
      pathParams,
      queryParams
    });
  }

  public async findById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { apVendorId: number };
  }): Promise<ApVendorDto> {
    const { options, pathParams } = params;
    return await this.sendGet<ApVendorDto>({
      apiEndpoint: `ap-vendors/:apVendorId`,
      options,
      pathParams
    });
  }

  public async findOneByVendorKeyAndApConfigId(params: {
    options?: ViveApiRequestOptions;
    pathParams: { vendorKey: string; apConfigId: number };
  }): Promise<ApVendorDto> {
    const { options, pathParams } = params;
    return await this.sendGet<ApVendorDto>({
      apiEndpoint: `ap-vendors/:vendorKey/ap-configs/:apConfigId`,
      options,
      pathParams
    });
  }

  public async updateById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { apVendorId: number };
    data: ApVendorsUpdateOneRequestDto;
  }): Promise<ApVendorDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<ApVendorDto>({
      apiEndpoint: `ap-vendors/:apVendorId`,
      options,
      pathParams,
      data
    });
  }

  public async deleteById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { apVendorId: number };
  }): Promise<void> {
    const { options, pathParams } = params;
    await this.sendDelete<void>({
      apiEndpoint: `ap-vendors/:apVendorId`,
      options,
      pathParams
    });
  }

  public async create(params: {
    options?: ViveApiRequestOptions;
    data: ApVendorsCreateRequestDto;
  }): Promise<ApVendorDto> {
    const { options, data } = params;
    return await this.sendPost<ApVendorDto>({
      apiEndpoint: `ap-vendors`,
      options,
      data
    });
  }

  public async upsert(params: {
    options?: ViveApiRequestOptions;
    data: ApVendorsUpsertRequestDto;
  }): Promise<ApVendorDto> {
    const { options, data } = params;
    return await this.sendPut<ApVendorDto>({
      apiEndpoint: `ap-vendors`,
      options,
      data
    });
  }

  public async linkToViveVendor(params: {
    options?: ViveApiRequestOptions;
    data: ApVendorsLinkToViveVendorRequestDto;
  }): Promise<void> {
    const { options, data } = params;
    await this.sendPost<void>({
      apiEndpoint: `ap-vendors/link-to-vive-vendor`,
      options,
      data
    });
  }

  public async findMatching(params: {
    options?: ViveApiRequestOptions;
    pathParams: { apConfigId: number };
    queryParams?: Omit<ApVendorsControllerFindMatchingV3Params, 'apConfigId'>;
  }): Promise<ApVendorDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<ApVendorDto>({
      apiEndpoint: `ap-vendors/ap-configs/:apConfigId/matching`,
      options,
      pathParams,
      queryParams
    });
  }

  public async getSyncedLinkedRecord(params: {
    options?: ViveApiRequestOptions;
    pathParams: { apConfigId: number };
    queryParams?: Omit<
      ApVendorsControllerGetSyncedLinkedRecordV3Params,
      'apConfigId'
    >;
  }): Promise<ApVendorsGetSyncedLinkedRecordResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<ApVendorsGetSyncedLinkedRecordResponseDto>({
      apiEndpoint: `ap-vendors/ap-configs/:apConfigId/synced-linked-record`,
      options,
      pathParams,
      queryParams
    });
  }

  public async mapToClientRegisteredVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { apConfigId: number };
    queryParams?: Omit<
      ApVendorsControllerMapToClientRegisteredVendorV3Params,
      'apConfigId'
    >;
  }): Promise<ApVendorsMapToClientRegisteredVendorResponseDto[]> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<
      ApVendorsMapToClientRegisteredVendorResponseDto[]
    >({
      apiEndpoint: `ap-vendors/ap-configs/:apConfigId/map-to-client-registered-vendor`,
      options,
      pathParams,
      queryParams
    });
  }

  public async mapToExemptVendor(params: {
    options?: ViveApiRequestOptions;
    pathParams: { apConfigId: number };
  }): Promise<ApVendorsMapToExemptVendorResponseDto[]> {
    const { options, pathParams } = params;
    return await this.sendGet<ApVendorsMapToExemptVendorResponseDto[]>({
      apiEndpoint: `ap-vendors/ap-configs/:apConfigId/map-to-exempt-vendor`,
      options,
      pathParams
    });
  }
}

export const apVendorsService = new ApVendorsService();
